// useAppInstances — the SHARED source of truth for "apps at this node" (F2 fix): both the
// sidebar Mounted-apps region and the Apps facet tab read the SAME wall-respecting instance
// list (fetchAppInstances, inherit-down, active-only), so the two surfaces can never tell
// contradicting stories about what is mounted.
import { useEffect, useState } from "react";
import { useScopedFetch, type ScopedState } from "../pages/scoped/useScopedFetch";
import { useScopeTreeStatus } from "./ContextChain";
import { canonicalSubAppId } from "@perlab/registry";
import {
  APP_INSTANCES_CHANGED_EVENT,
  fetchAppInstances,
  type AppInstance,
} from "../api/appInstances";

export function useAppInstances(readerScopeNodeId: string): ScopedState<AppInstance[]> {
  // Nothing is fetched until the scope tree has settled.
  //
  // All four callers derive their node id from the ContextChain scalars, which hold
  // DEFAULT_SCOPE (ContextChain.tsx:215) until `/scope/nodes` answers. On any environment that
  // did not run seed_scope_demo.py those nodes are not there, so the shell fired
  // `GET /app-instances?reader_scope_node_id=anand-demo` on every mount and took four 404s that
  // said nothing about the estate. perl-integration.spec.ts fails on exactly those.
  //
  // `useScopeData.ready` closes the same window for everything that goes through fetchScoped;
  // this hook does not — `fetchAppInstances` builds its own URL (api/appInstances.ts:33) — so the
  // gate has to be here as well. It is one hook rather than four call sites on purpose: the
  // comment at the top of this file calls it the SHARED source of truth, and a readiness rule
  // that lived in the callers could be got wrong in one of them.
  //
  // An ERRORED tree counts as settled, for the same reason it does in useScopeData: a failed
  // /scope/nodes is not an authoritative empty tree, and refusing to load the rail behind a fetch
  // that will not succeed is worse than the 404s it avoids.
  const scopeTreeStatus = useScopeTreeStatus();
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const refresh = (event: Event): void => {
      const scopeNodeId = (event as CustomEvent<{ scopeNodeId?: string }>).detail?.scopeNodeId;
      if (!scopeNodeId || scopeNodeId === readerScopeNodeId) setRevision((value) => value + 1);
    };
    window.addEventListener(APP_INSTANCES_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(APP_INSTANCES_CHANGED_EVENT, refresh);
  }, [readerScopeNodeId]);
  const state = useScopedFetch(
    async () => ({
      readerScopeNodeId,
      instances:
        scopeTreeStatus === "loading" ? null : await fetchAppInstances(readerScopeNodeId),
    }),
    [readerScopeNodeId, revision, scopeTreeStatus],
    scopeTreeStatus === "loading" ? undefined : {
      key: ["app-instances", readerScopeNodeId],
    },
  );
  if (state.kind !== "ready") return state;
  // The pre-hydration pass resolves with no instances rather than with an empty list: an empty
  // list is an answer ("nothing is mounted here"), and this is the absence of one.
  if (state.data.instances === null) return { kind: "loading" };
  // URL params update one render before useScopedFetch starts the replacement request. Never return
  // the previous scope's placements during that transition; callers treat this as a normal load.
  if (state.data.readerScopeNodeId !== readerScopeNodeId) return { kind: "loading" };
  return { kind: "ready", data: state.data.instances };
}

/**
 * One row per app: when the same app has several visible instances (e.g. mounted at the
 * workspace AND at the engagement, both inherit-down visible), the DEEPEST mount (longest
 * scope_path) wins — the placement closest to where the reader stands is the door target.
 */
export function dedupeDeepestByApp(instances: readonly AppInstance[]): AppInstance[] {
  const depth = (i: AppInstance): number => i.scope_path.split("/").filter(Boolean).length;
  const best = new Map<string, AppInstance>();
  for (const i of instances) {
    const appId = canonicalSubAppId(i.app_id);
    const normalized = appId === i.app_id ? i : { ...i, app_id: appId };
    const cur = best.get(appId);
    if (!cur || depth(normalized) > depth(cur)) best.set(appId, normalized);
  }
  return [...best.values()];
}
